// src/pages/ProfitLossReport/index.js
import React, { useRef } from "react";
import { useSelector } from "react-redux";
import { DownloadOutlined, FileExcelOutlined, PrinterOutlined } from "@ant-design/icons";
import { App, Button, Card, Flex, Grid, Typography } from "antd";
import ProfitLossFilters from "./components/ProfitLossFilters";
import ProfitLossTable from "./components/ProfitLossTable";
import ReportDom from "../../../components/common/ReportDom";
import { useReactToPrint } from "react-to-print";
import { exportProfitLossCsv } from "./components/exportProfitLossCsv";
import { exportProfitLossXlsx } from "./components/exportProfitLossXlsx";
import { useProfitLossData } from "./components/useProfitLossData";
import dayjs from "dayjs";
import { toCompanyDateOnlyforReportDateFilter } from "../../../lib/functions";

const { Title } = Typography;

const fmtDate = (d) => toCompanyDateOnlyforReportDateFilter(d)?.format("DD-MM-YYYY");

// Period line for the printed report, based on the filters the report was loaded with.
const getPrintDateLabel = (applied) => {
  if (!applied) return undefined;
  if (applied.filterType === "Date Wise") {
    if (applied.fromDate && applied.toDate) return `${fmtDate(applied.fromDate)} to ${fmtDate(applied.toDate)}`;
    if (applied.toDate) return `Up to ${fmtDate(applied.toDate)}`;
    return undefined;
  }
  if (applied.filterType === "Lifetime") {
    return `Life Time (up to ${dayjs().format("DD-MM-YYYY")})`;
  }
  return undefined;
};

// Zoho-style period line for the Excel export ("From 01/09/2026 To 30/09/2026").
const exportDate = (d) => toCompanyDateOnlyforReportDateFilter(d)?.format("DD/MM/YYYY");
const getExportDateLabel = (applied) => {
  if (!applied) return "";
  if (applied.filterType === "Date Wise") {
    if (applied.fromDate && applied.toDate) return `From ${exportDate(applied.fromDate)} To ${exportDate(applied.toDate)}`;
    if (applied.toDate) return `As of ${exportDate(applied.toDate)}`;
    return "";
  }
  if (applied.filterType === "Lifetime") {
    return `As of ${dayjs().format("DD/MM/YYYY")}`;
  }
  return "";
};

const ProfitLossReport = () => {
  const { message } = App.useApp();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();
  const { balances, appliedFilters } = useSelector((state) => state.profitLoss);
  const printDateLabel = getPrintDateLabel(appliedFilters);

  const reportRef = useRef(null);
  const { rows } = useProfitLossData();

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: "Profit Loss Report",
    removeAfterPrint: true,
  });
  const exportParams = () => ({
    rows,
    title: "Profit and Loss",
    basis: "Accrual",
    dateLabel: getExportDateLabel(appliedFilters),
    filename: "profit_loss",
  });
  const handleDownloadCsv = () => exportProfitLossCsv(exportParams());
  const handleDownloadExcel = async () => {
    try {
      await exportProfitLossXlsx(exportParams());
    } catch (err) {
      message.error("Failed to export Excel file");
    }
  };

  const hasData = Object.keys(balances).length > 0;
  return (
    <div className="p-4">
      <Card
        className="shadow-md rounded-xl mb-4"
        title={
          <Title level={4} style={{ margin: 0 }} className="reportBigTitle" >
            Profit Loss Report
          </Title>
        }
      extra={
          <Flex gap={8}>
            {hasData && (
              <Button icon={<DownloadOutlined />} onClick={handleDownloadCsv}>
                {!screens.xs && "CSV"}
              </Button>
            )}
            {hasData && (
              <Button icon={<FileExcelOutlined />} onClick={handleDownloadExcel}>
                {!screens.xs && "Excel"}
              </Button>
            )}
            <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
              {!screens.xs && "Print"}
            </Button>
          </Flex>
        }
        styles={{
          header: { borderBottom: "none", padding: "12px 16px" },
          body: { padding: 0 },
        }}
      />

      <ProfitLossFilters messageApi={message} />

      <Card className="shadow-md rounded-xl">
        <ProfitLossTable />
      </Card>

      {/* Hidden print layout */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title="Profit Loss Report"
          mode="report-page"
          filters={null}
          dateLabel={printDateLabel}
          renderTable={() => (
            <ProfitLossTable printMode={true} />
          )}
        />
      </div>
    </div>
  );
};

export default ProfitLossReport;
